import { AnalysisVariable } from "@models/platform-analysis/enums/variableName";
import { template } from "lodash";

export class AnalysisFeeCalculatorErrorMessages {
  static nullableFieldMessage = (variable: keyof typeof AnalysisVariable) =>
    `"<%= ${AnalysisVariable[variable]} ?? '' %>"`;

  static readonly TotalBalanceExceedAccountBalance = {
    plainText:
      "Total balance in this section exceeds the total account balances specified above",
  };

  static readonly SuperCashBalanceExceedAccountBalance = {
    plainText:
      "Cash balance for Super accounts must be smaller than the total balance of Super accounts specified above",
  };

  static readonly IDPSCashBalanceExceedAccountBalance = {
    plainText:
      "Cash balance for IDPS accounts must be smaller than the total balance of IDPS accounts specified above",
  };

  static readonly HoldingSizeCannotBeNegative = {
    plainText: "Holding size cannot be negative",
  };

  static readonly NoAccountBalances = {
    plainText: "At least one account must have a balance",
  };

  static readonly EdgeBalanceExceedManagedAccount = {
    executor: template(`The total amount entered for 
      ${this.nullableFieldMessage(
        AnalysisVariable.Edge_A100_Portfolio_balance,
      )} and 
      ${this.nullableFieldMessage(
        AnalysisVariable.Edge_A30_Portfolio_balance,
      )} cannot exceed the amount specified above for 
      ${this.nullableFieldMessage(AnalysisVariable.MA_balance_i)}`),
    variables: [
      AnalysisVariable.Edge_A100_Portfolio_balance,
      AnalysisVariable.Edge_A30_Portfolio_balance,
      AnalysisVariable.MA_balance_i,
    ],
  };

  static readonly EdgeEligibleBalanceExceedManagedFund = {
    executor: template(`The amount entered for 
      ${this.nullableFieldMessage(
        AnalysisVariable.Edge_eligible_MF_balance,
      )} cannot exceed the amount specified above for
        ${this.nullableFieldMessage(AnalysisVariable.MF_balance_i)}`),
    variables: [
      AnalysisVariable.Edge_eligible_MF_balance,
      AnalysisVariable.MF_balance_i,
    ],
  };

  static readonly NorthEligibleBalanceExceedManagedAccountAndFund = {
    executor: template(`The amount entered for 
      ${this.nullableFieldMessage(
        AnalysisVariable.NorthSelect_balance,
      )} cannot exceed the amount specified above for
        ${this.nullableFieldMessage(AnalysisVariable.MF_balance_i)} and 
        ${this.nullableFieldMessage(AnalysisVariable.MA_balance_i)}`),
    variables: [
      AnalysisVariable.NorthSelect_balance,
      AnalysisVariable.MF_balance_i,
      AnalysisVariable.MA_balance_i,
    ],
  };

  static readonly CPSCoreBalanceExceedTotalBalance = {
    executor: template(`The amount entered for 
      ${this.nullableFieldMessage(
        AnalysisVariable.CPSCore_balance,
      )} cannot exceed the amount specified above for
        ${this.nullableFieldMessage(AnalysisVariable.MF_balance_i)} and 
        ${this.nullableFieldMessage(AnalysisVariable.MA_balance_i)}`),
    variables: [
      AnalysisVariable.CPSCore_balance,
      AnalysisVariable.MF_balance_i,
      AnalysisVariable.MA_balance_i,
    ],
  };

  static getMaximumAccountsExceededMessage = (maximumAccounts: number) => {
    return `Maximum ${maximumAccounts} accounts supported. Please remove one`;
  };

  static readonly InvalidPortfolioSetup = "Portfolio setup is invalid";

  static readonly MaximumPortfolioSelectionsReached =
    "Portfolio limit reached. Click Next, Back or Exit to clear the unused portfolios.";

  static readonly BelowMinimumInvestmentSize =
    "Allocations below minimum investment size";

  static readonly AllocationMustSumTo100 = "Allocation must sum to 100%";
  static readonly MissingInvestmentOption = "Missing investment option";
  static readonly NoIDPSBalanceIncluded =
    "No IDPS accounts included in the analysis";
  static readonly NoSuperBalanceIncluded =
    "No Super/Pension accounts included in the analysis";
  static readonly MissingInvestment = "Missing investment";
  static readonly NotAvailableInIDPS = "Not available in IDPS";
  static readonly NotAvailableInSuper = "Not available in Super/Pension";
  static readonly NotAvailableInIDPSAndSuper =
    "Not available in IDPS and Super";
  static readonly InvestmentOptionNotAvailable =
    "Investment option not available in IDPS/Super";
  static readonly InvestmentNotAvailable =
    "Investment not available in IDPS/Super";
  static readonly CashNotAvailableInSuperError =
    "Cash in Super/Pension accounts is not available";
}

export class AnalysisFeeCalculatorSectionTitles {
  maximumAccounts: number;
  mainTitles: AnalysisFeeCalculatorSectionMainTitles;
  descriptions: AnalysisFeeCalculatorSectionDescriptions;

  constructor({ maximumAccounts }: { maximumAccounts: number }) {
    this.maximumAccounts = maximumAccounts;
    this.mainTitles = new AnalysisFeeCalculatorSectionMainTitles({
      maximumAccounts,
    });
    this.descriptions = new AnalysisFeeCalculatorSectionDescriptions({
      maximumAccounts,
    });
  }

  getAccountSetupTitles = () => {
    return {
      main: this.mainTitles.getAccountSetup(),
      subTitle: this.descriptions.getAccountSetup(),
    };
  };

  getPortfolioLevelTitles = () => {
    return {
      main: AnalysisFeeCalculatorSectionMainTitles.PortfolioLevel,
      subTitle: AnalysisFeeCalculatorSectionDescriptions.PortfolioLevel,
    };
  };

  getInvestmentOptionsTitles = () => {
    return {
      main: AnalysisFeeCalculatorSectionMainTitles.InvestmentOptions,
      subTitle: AnalysisFeeCalculatorSectionDescriptions.InvestmentOptions,
    };
  };

  getPortfolioSetupTitles = () => {
    return {
      main: AnalysisFeeCalculatorSectionMainTitles.PortfolioSetup,
      subTitle: AnalysisFeeCalculatorSectionDescriptions.PortfolioSetup,
    };
  };

  getHoldingSizesTitles = () => {
    return {
      main: AnalysisFeeCalculatorSectionMainTitles.HoldingSizes,
      subTitle: AnalysisFeeCalculatorSectionDescriptions.HoldingSizes,
    };
  };

  getTransactionOutsideTitles = () => {
    return {
      main: AnalysisFeeCalculatorSectionMainTitles.TransactionOutside,
      subTitle: AnalysisFeeCalculatorSectionDescriptions.TransactionOutside,
    };
  };

  getCFSEdgeOptionalTitles = () => {
    return {
      main: AnalysisFeeCalculatorSectionMainTitles.CFSEdgeOptional,
      subTitle: AnalysisFeeCalculatorSectionDescriptions.CFSEdgeOptional,
    };
  };

  getCPSOptionalTitles = () => {
    return {
      main: AnalysisFeeCalculatorSectionMainTitles.CPSOptional,
      subTitle: AnalysisFeeCalculatorSectionDescriptions.CPSOptional,
    };
  };

  getNorthOptionalTitles = () => {
    return {
      main: AnalysisFeeCalculatorSectionMainTitles.NorthOptional,
      subTitle: AnalysisFeeCalculatorSectionDescriptions.NorthOptional,
    };
  };

  getHoldingNumberTitles = () => {
    return {
      main: AnalysisFeeCalculatorSectionMainTitles.HoldingNumber,
      subTitle: AnalysisFeeCalculatorSectionDescriptions.HoldingNumber,
    };
  };

  getTransactionWithinTitles = () => {
    return {
      main: AnalysisFeeCalculatorSectionMainTitles.TransactionWithin,
      subTitle: AnalysisFeeCalculatorSectionDescriptions.TransactionWithin,
    };
  };

  getRetailInsuranceTitles = () => {
    return {
      main: AnalysisFeeCalculatorSectionMainTitles.RetailInsurance,
      subTitle: AnalysisFeeCalculatorSectionDescriptions.RetailInsurance,
    };
  };
}

class AnalysisFeeCalculatorSectionMainTitles {
  maximumAccounts: number;

  constructor({ maximumAccounts }: { maximumAccounts: number }) {
    this.maximumAccounts = maximumAccounts;
  }

  // Account setup
  static readonly AccountSetupTemplate = template(
    "What do you expect the average holding balance of each account will be for the next 12 months? (Maximum <%= maximumAccounts %> accounts)",
  );
  getSupplierAccountSetup = () =>
    AnalysisFeeCalculatorSectionMainTitles.AccountSetupTemplate({
      maximumAccounts: this.maximumAccounts,
    });
  getAdviserAccountSetup = this.getSupplierAccountSetup;

  // Portfolio level
  static readonly SupplierPortfolioLevel =
    "Include a portfolio of SMAs in the fee analysis?";
  static readonly AdviserPortfolioLevel = this.SupplierPortfolioLevel;

  // Investment options
  static readonly SupplierInvestmentOptions =
    "Which of the following will be held through the platform?";
  static readonly AdviserInvestmentOptions = this.SupplierInvestmentOptions;

  // Portfolio setup
  static readonly SupplierAccountSetup =
    "SMA portfolio setup: Add or edit client portfolios for each platform";
  static readonly AdviserAccountSetup = this.SupplierAccountSetup;

  // CFS Edge
  static readonly SupplierCFSEdgeOptional =
    "CFS Edge: Across all accounts, approximately how much do you expect to be held in alternate admin fee menus? (Optional)";
  static readonly AdviserCFSEdgeOptional = this.SupplierCFSEdgeOptional;

  // North
  static readonly SupplierNorthOptional =
    "North: Across all accounts, approximately how much do you expect to be held in alternate admin fee menus? (Optional)";
  static readonly AdviserNorthOptional = this.SupplierNorthOptional;

  // CPS
  static readonly SupplierCPSOptional =
    "Cornerstone Portfolio Service: Across all accounts, approximately how much do you expect to be held in the Core investment menu? (Optional)";
  static readonly AdviserCPSOptional = this.SupplierCPSOptional;

  // Holding sizes
  static readonly SupplierHoldingSizes =
    "Across all accounts, approximately how much do you expect will be held in each investment class?";
  static readonly AdviserHoldingSizes = this.SupplierHoldingSizes;

  // Holding numbers
  static readonly SupplierHoldingNumber =
    "Macquarie Manager: How many different funds, listed investments and managed account models will be held across all accounts?";
  static readonly AdviserHoldingNumber = this.SupplierHoldingNumber;

  // Transaction outside
  static readonly SupplierTransactionOutside =
    "In the next 12 months, what is the total number of transactions you expect <u>outside</u> SMAs/MDAs, and what is your anticipated average transaction size?";
  static readonly AdviserTransactionOutside = this.SupplierTransactionOutside;

  // Transaction within
  static readonly SupplierTransactionWithin = `<p>In the next 12 months, what is the total number of transactions you expect <u>within</u> the SMAs/MDAs your client holds, 
   and what is your anticipated average transaction size?</p>`;
  static readonly AdviserTransactionWithin = this.SupplierTransactionWithin;

  // Retail insurance
  static readonly SupplierRetailInsurance =
    "The retail insurance policies that will be held through platform accounts:";
  static readonly AdviserRetailInsurance = this.SupplierRetailInsurance;

  // Getters
  getAccountSetup() {
    return {
      supplier: this.getSupplierAccountSetup(),
      adviser: this.getAdviserAccountSetup(),
    };
  }

  static readonly PortfolioLevel = {
    supplier: AnalysisFeeCalculatorSectionMainTitles.SupplierPortfolioLevel,
    adviser: AnalysisFeeCalculatorSectionMainTitles.AdviserPortfolioLevel,
  };

  static readonly InvestmentOptions = {
    supplier: AnalysisFeeCalculatorSectionMainTitles.SupplierInvestmentOptions,
    adviser: AnalysisFeeCalculatorSectionMainTitles.AdviserInvestmentOptions,
  };

  static readonly PortfolioSetup = {
    supplier: AnalysisFeeCalculatorSectionMainTitles.SupplierAccountSetup,
    adviser: AnalysisFeeCalculatorSectionMainTitles.AdviserAccountSetup,
  };

  static readonly CFSEdgeOptional = {
    supplier: AnalysisFeeCalculatorSectionMainTitles.SupplierCFSEdgeOptional,
    adviser: AnalysisFeeCalculatorSectionMainTitles.AdviserCFSEdgeOptional,
  };

  static readonly NorthOptional = {
    supplier: AnalysisFeeCalculatorSectionMainTitles.SupplierNorthOptional,
    adviser: AnalysisFeeCalculatorSectionMainTitles.AdviserNorthOptional,
  };

  static readonly CPSOptional = {
    supplier: AnalysisFeeCalculatorSectionMainTitles.SupplierCPSOptional,
    adviser: AnalysisFeeCalculatorSectionMainTitles.AdviserCPSOptional,
  };

  static readonly HoldingSizes = {
    supplier: AnalysisFeeCalculatorSectionMainTitles.SupplierHoldingSizes,
    adviser: AnalysisFeeCalculatorSectionMainTitles.AdviserHoldingSizes,
  };

  static readonly HoldingNumber = {
    supplier: AnalysisFeeCalculatorSectionMainTitles.SupplierHoldingNumber,
    adviser: AnalysisFeeCalculatorSectionMainTitles.AdviserHoldingNumber,
  };

  static readonly TransactionOutside = {
    supplier: AnalysisFeeCalculatorSectionMainTitles.SupplierTransactionOutside,
    adviser: AnalysisFeeCalculatorSectionMainTitles.AdviserTransactionOutside,
  };

  static readonly TransactionWithin = {
    supplier: AnalysisFeeCalculatorSectionMainTitles.SupplierTransactionWithin,
    adviser: AnalysisFeeCalculatorSectionMainTitles.AdviserTransactionWithin,
  };

  static readonly RetailInsurance = {
    supplier: AnalysisFeeCalculatorSectionMainTitles.SupplierRetailInsurance,
    adviser: AnalysisFeeCalculatorSectionMainTitles.AdviserRetailInsurance,
  };
}

class AnalysisFeeCalculatorSectionDescriptions {
  maximumAccounts: number;

  constructor({ maximumAccounts }: { maximumAccounts: number }) {
    this.maximumAccounts = maximumAccounts;
  }

  // Account setup
  static readonly SupplierAccountSetupTemplate = template(
    `<p>Please enter the approximate value, and consider any deposits and withdrawals you expect in the coming year.</p><br/><p>
    Note: The fee calculator currently supports a maximum of <%= maximumAccounts %> accounts.</p>`,
  );
  getSupplierAccountSetup = () =>
    AnalysisFeeCalculatorSectionDescriptions.SupplierAccountSetupTemplate({
      maximumAccounts: this.maximumAccounts,
    });
  getAdviserAccountSetup = this.getSupplierAccountSetup;

  // Portfolio level
  static readonly SupplierPortfolioLevel = `<p>Choose whether to factor in a portfolio of SMAs within the fee analysis. 
    Including the portfolio means the disclosed fees of SMAs will be added to the total platform cost. 
    You may enter the portfolio either at the <strong><u>family group level</u></strong> or at the individual <strong><u>account level</u></strong>, 
    depending on your preferred scope of analysis.</p>`;
  static readonly AdviserPortfolioLevel = this.SupplierPortfolioLevel;

  // Investment options
  static readonly SupplierInvestmentOptions =
    "Please select all the different solutions you expect your client(s) to hold. This will determine which fees we include in our modelling.";
  static readonly AdviserInvestmentOptions = this.SupplierInvestmentOptions;

  // Portfolio setup
  static readonly SupplierPortfolioSetup = `<p>Portfolios are assumed to be held in the same allocation by all accounts in the family.</p>
    <br /><p>Once you add your first portfolio, it will be named ‘Portfolio 1’. You can assign this to other platforms from the dropdown menu or you can add a new portfolio for each platform. Subsequent portfolios will be named sequentially, such as ‘Portfolio 2’.</p>
    <br /><p>If you edit an existing portfolio, it will be saved as a new portfolio. For example, if ‘Portfolio 1’ and ‘Portfolio 2’ exist, editing ‘Portfolio 1’ will create a new version named ‘Portfolio 3’. You can still allocate the original ‘Portfolio 1’ to another platform.</p>
    <br /><p>When you leave this page, all unassigned portfolios will be deleted.</p>`;
  static readonly AdviserPortfolioSetup = this.SupplierPortfolioSetup;

  // CFS Edge
  static readonly SupplierCFSEdgeOptional = `<p>Platform admin fees on CFS Edge are reduced based on the proportion of the portfolio invested in the Accelerate menu and managed funds eligible for no admin fees.</p>
    <br /><p>Please estimate the average holding in these menus over the next 12 months. 
    If you leave these fields blank, we will not include these investments in our fee estimates.</p>`;
  static readonly AdviserCFSEdgeOptional = this.SupplierCFSEdgeOptional;

  // North
  static readonly SupplierNorthOptional = `<p>North platform admin fees are reduced based on the proportion of the portfolio invested in the Select and Grow menus.</p>
    <br /><p>Please estimate the average holding in this menu over the next 12 months. 
    If you leave these fields blank, we will not include these investments in our fee estimates.</p>`;
  static readonly AdviserNorthOptional = this.SupplierNorthOptional;

  // CPS
  static readonly SupplierCPSOptional = `<p>Platform admin fees on Cornerstone Portfolio Service are reduced based on the proportion of the portfolio invested in the Core menu, which includes Cornerstone Portfolios.</p><br />
    <p>Please estimate the average holding in this menu over the next 12 months. If you leave these fields blank, we will not include these investments in our fee estimates.</p>`;
  static readonly AdviserCPSOptional = this.SupplierCPSOptional;

  // Holding sizes
  static readonly SupplierHoldingSizes = `<p>Please estimate the average total holding size for the next 12 months.</p><br /><p>
    For non-managed account options, specify the amount that will be held <strong><u>outside</u></strong> managed accounts only. 
    For example, if there are managed funds held both outside and inside managed accounts, in the ‘Managed funds’ field only enter the value that is held outside managed accounts.</p><br /><p>
    We will pro rata the fees charged for holding the non-cash investments across your client's IDPS and Super accounts. 
    If you leave these fields blank, we will not include the cost of holding these investments in our fee estimates.</p>`;
  static readonly AdviserHoldingSizes = this.SupplierHoldingSizes;

  // Holding numbers
  static readonly SupplierHoldingNumber = `Please estimate the total number of holdings. 
    For example, if you have a portfolio with eight managed accounts plus shares in one company, the value will be 9. 
    The figure you provide will affect the fee tiering used by some platforms. 
    If you leave these fields blank, some admin fees may appear slightly lower than they actually are.`;
  static readonly AdviserHoldingNumber = this.SupplierHoldingNumber;

  // Transaction outside
  static readonly SupplierTransactionOutside = `<p>Please count all transactions that you expect will happen <u>outside</u> the managed account structure. 
    This includes any ad hoc trades you place plus any transactions from automatic buy, sell and rebalancing facilities. 
    We will use this to estimate the transaction fees charged. 
    If you leave these fields blank, we will not include transaction fees in our fee estimates</p>`;
  static readonly AdviserTransactionOutside = this.SupplierTransactionOutside;

  // Transaction within
  static readonly SupplierTransactionWithin = `<p>Please count all transactions that you expect will happen <u>within</u> the managed account structure. 
    Most platforms have a different fee for transactions that occur within managed accounts than for those outside managed accounts. 
    This can be difficult for you to estimate, as each manager varies in their trading activity. 
    If you leave these fields blank, we will not include managed account transaction fees in our fee estimates.</p>`;
  static readonly AdviserTransactionWithin = this.SupplierTransactionWithin;

  // Retail insurance
  static readonly SupplierRetailInsurance =
    "Some platforms charge an admin fee for holding insurance. If you leave these fields blank, we will not include the admin fees on insurance in our fee estimates.";
  static readonly AdviserRetailInsurance = this.SupplierRetailInsurance;

  // Getters
  getAccountSetup = () => {
    return {
      supplier: this.getSupplierAccountSetup(),
      adviser: this.getAdviserAccountSetup(),
    };
  };

  static readonly PortfolioLevel = {
    supplier: AnalysisFeeCalculatorSectionDescriptions.SupplierPortfolioLevel,
    adviser: AnalysisFeeCalculatorSectionDescriptions.AdviserPortfolioLevel,
  };

  static readonly InvestmentOptions = {
    supplier:
      AnalysisFeeCalculatorSectionDescriptions.SupplierInvestmentOptions,
    adviser: AnalysisFeeCalculatorSectionDescriptions.AdviserInvestmentOptions,
  };

  static readonly HoldingSizes = {
    supplier: AnalysisFeeCalculatorSectionDescriptions.SupplierHoldingSizes,
    adviser: AnalysisFeeCalculatorSectionDescriptions.AdviserHoldingSizes,
  };

  static readonly PortfolioSetup = {
    supplier: AnalysisFeeCalculatorSectionDescriptions.SupplierPortfolioSetup,
    adviser: AnalysisFeeCalculatorSectionDescriptions.AdviserPortfolioSetup,
  };

  static readonly CFSEdgeOptional = {
    supplier: AnalysisFeeCalculatorSectionDescriptions.SupplierCFSEdgeOptional,
    adviser: AnalysisFeeCalculatorSectionDescriptions.AdviserCFSEdgeOptional,
  };

  static readonly NorthOptional = {
    supplier: AnalysisFeeCalculatorSectionDescriptions.SupplierNorthOptional,
    adviser: AnalysisFeeCalculatorSectionDescriptions.AdviserNorthOptional,
  };

  static readonly CPSOptional = {
    supplier: AnalysisFeeCalculatorSectionDescriptions.SupplierCPSOptional,
    adviser: AnalysisFeeCalculatorSectionDescriptions.AdviserCPSOptional,
  };

  static readonly HoldingNumber = {
    supplier: AnalysisFeeCalculatorSectionDescriptions.SupplierHoldingNumber,
    adviser: AnalysisFeeCalculatorSectionDescriptions.AdviserHoldingNumber,
  };

  static readonly TransactionOutside = {
    supplier:
      AnalysisFeeCalculatorSectionDescriptions.SupplierTransactionOutside,
    adviser: AnalysisFeeCalculatorSectionDescriptions.AdviserTransactionOutside,
  };

  static readonly TransactionWithin = {
    supplier:
      AnalysisFeeCalculatorSectionDescriptions.SupplierTransactionWithin,
    adviser: AnalysisFeeCalculatorSectionDescriptions.AdviserTransactionWithin,
  };

  static readonly RetailInsurance = {
    supplier: AnalysisFeeCalculatorSectionDescriptions.SupplierRetailInsurance,
    adviser: AnalysisFeeCalculatorSectionDescriptions.AdviserRetailInsurance,
  };
}
